import { useState, type FormEvent } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { apiErrorDetail } from '@/lib/api/fraud-client'
import { useCreateFraudAllowance, useFraudAllowances, useLiftFraudBlock, useRevokeFraudAllowance } from '@/lib/api/fraud-queries'
import type { CreateFraudAllowanceInput, FraudActiveBlock, FraudAllowance } from '@/lib/api/fraud-types'
import { usePermission } from '@/lib/permissions'
import { cn } from '@/lib/utils'
import { Banner, Spinner } from './shared'
import { EMPTY, PILL, ROW, TD, TH, fmtDateTime, kindLabel } from './format'
import { cardText } from './card-brand'

const FIELD = 'w-full rounded-md border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-sm'

function describe(a: FraudAllowance): string {
  if (a.kind === 'attendee') return a.user_name || (a.user ? `User #${a.user}` : 'Deleted account')
  if (a.card) return cardText(a.card.brand, a.card.last4)
  return a.instrument_id || 'Card (by fingerprint)'
}

/**
 * Trust an attendee or a card at this event. Used from the Trusted tab
 * (target typed in) and from the attendee page (attendee id fixed, and
 * the attendee's active block passed in: a block always wins over trust,
 * so an event-level one can be lifted in the same step).
 */
export function TrustForm({ eventId, userId, activeBlock, onDone }: {
  eventId: number
  userId?: number
  activeBlock?: FraudActiveBlock | null
  onDone?: (allowance: FraudAllowance) => void
}) {
  const create = useCreateFraudAllowance(eventId)
  const lift = useLiftFraudBlock(eventId)
  const canLiftHere = !!activeBlock && activeBlock.scope === 'event'
  const [liftBlock, setLiftBlock] = useState(true)
  const [kind, setKind] = useState<'attendee' | 'card'>('attendee')
  const [target, setTarget] = useState(userId ? String(userId) : '')
  const [reason, setReason] = useState('')
  const [expiresAt, setExpiresAt] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [created, setCreated] = useState<FraudAllowance | null>(null)
  const fixed = !!userId

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setCreated(null)
    const value = target.trim()
    const input: CreateFraudAllowanceInput = { reason, expires_at: expiresAt ? new Date(expiresAt).toISOString() : null }
    if (fixed || kind === 'attendee') {
      const id = Number(value)
      if (!id) {
        setError('Enter the attendee id.')
        return
      }
      input.user = id
    } else {
      if (!value) {
        setError('Enter the card instrument id (PI…), as shown on the attendee page.')
        return
      }
      input.instrument_id = value
    }
    let allowance: FraudAllowance
    try {
      allowance = await create.mutateAsync(input)
    } catch (err) {
      setError(apiErrorDetail(err, 'Could not trust this target.'))
      return
    }
    if (canLiftHere && liftBlock && activeBlock) {
      // Trust is already in place: a failed lift must not read as a failed trust.
      try {
        await lift.mutateAsync({ blockId: activeBlock.id, notes: 'Lifted when the attendee was trusted' })
        allowance = { ...allowance, warning: null }
      } catch (err) {
        allowance = {
          ...allowance,
          warning: `the block could not be lifted (${apiErrorDetail(err, 'lift failed')}). Lift it from the Blocks tab; until then the attendee stays refused.`,
        }
      }
    }
    setCreated(allowance)
    setReason('')
    if (!fixed) setTarget('')
    onDone?.(allowance)
  }

  return (
    <form onSubmit={submit} className="space-y-3">
      {error && <Banner kind="error">{error}</Banner>}
      {created && !created.warning && <Banner kind="success">{describe(created)} is trusted at this event.</Banner>}
      {created?.warning && <Banner kind="info">{describe(created)} is trusted at this event, but: {created.warning}</Banner>}
      {activeBlock && (
        canLiftHere ? (
          <label className="flex items-start gap-2 text-sm text-neutral-700 dark:text-neutral-300">
            <input type="checkbox" checked={liftBlock} onChange={(e) => setLiftBlock(e.target.checked)} className="mt-0.5" />
            <span>
              <span className="font-medium">Also lift the current block</span> ({activeBlock.reason.replace(/_/g, ' ')}, since {fmtDateTime(activeBlock.created_at)}).
              A block always wins over trust; leave this ticked or the attendee stays refused.
            </span>
          </label>
        ) : (
          <Banner kind="info">
            This attendee is blocked at the {activeBlock.scope} level. Trust will not take effect until that block is lifted by
            {activeBlock.scope === 'organization' ? ' an organization administrator' : ' EventPay staff'}.
          </Banner>
        )
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {!fixed && (
          <label className="text-sm">
            <span className="block mb-1 text-neutral-700 dark:text-neutral-300">Trust</span>
            <select value={kind} onChange={(e) => setKind(e.target.value as 'attendee' | 'card')} className={FIELD}>
              <option value="attendee">An attendee (whole account at this event)</option>
              <option value="card">A card (any account using it here)</option>
            </select>
          </label>
        )}
        {!fixed && (
          <label className="text-sm">
            <span className="block mb-1 text-neutral-700 dark:text-neutral-300">{kind === 'attendee' ? 'Attendee id' : 'Card instrument id'}</span>
            <Input value={target} onChange={(e) => setTarget(e.target.value)} inputMode={kind === 'attendee' ? 'numeric' : 'text'} placeholder={kind === 'attendee' ? 'User id' : 'PI…'} />
          </label>
        )}
        <label className="text-sm">
          <span className="block mb-1 text-neutral-700 dark:text-neutral-300">Expires (optional)</span>
          <Input type="datetime-local" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
        </label>
        <label className={cn('text-sm', fixed ? '' : 'sm:col-span-2')}>
          <span className="block mb-1 text-neutral-700 dark:text-neutral-300">Reason</span>
          <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Sponsor, staff, known regular, verified at the desk" />
        </label>
      </div>
      <p className="text-xs text-neutral-500 dark:text-neutral-400">
        A trusted attendee or card is not scored: no 3-D Secure challenge, no refusal by the risk policy, no transfer lock. Blocks still apply.
      </p>
      <Button type="submit" size="sm" disabled={create.isPending || lift.isPending}>{create.isPending || lift.isPending ? 'Saving…' : canLiftHere && liftBlock ? 'Trust and lift the block' : 'Trust'}</Button>
    </form>
  )
}

export function RevokeButton({ eventId, allowance }: { eventId: number; allowance: Pick<FraudAllowance, 'id'> }) {
  const revoke = useRevokeFraudAllowance(eventId)
  const [open, setOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!open) {
    return <Button variant="outline" size="sm" onClick={() => setOpen(true)}>Revoke</Button>
  }
  return (
    <div className="flex flex-col gap-1 items-end">
      {error && <span className="text-xs text-red-600 dark:text-red-400">{error}</span>}
      <div className="flex gap-2">
        <Button
          size="sm"
          variant="destructive"
          disabled={revoke.isPending}
          onClick={async () => {
            setError(null)
            try {
              await revoke.mutateAsync(allowance.id)
              setOpen(false)
            } catch (err) {
              setError(apiErrorDetail(err, 'Could not revoke.'))
            }
          }}
        >
          {revoke.isPending ? 'Revoking…' : 'Confirm revoke'}
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>Cancel</Button>
      </div>
    </div>
  )
}

export function TrustedTab({ eventId }: { eventId: number }) {
  const canManage = usePermission('can_manage_fraud')
  const [activeOnly, setActiveOnly] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const { data, isLoading, error } = useFraudAllowances(eventId, activeOnly ? { active: true } : undefined)
  const rows = data ?? []

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <label className="flex items-center gap-2 text-sm text-neutral-700 dark:text-neutral-300">
          <input type="checkbox" checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)} />
          Active only
        </label>
        {canManage && (
          <Button size="sm" variant={showForm ? 'outline' : 'default'} onClick={() => setShowForm((v) => !v)}>
            {showForm ? 'Close' : 'Trust an attendee or card'}
          </Button>
        )}
      </div>

      {showForm && canManage && (
        <Card>
          <CardHeader><CardTitle className="text-base">Trust at this event</CardTitle></CardHeader>
          <CardContent>
            <TrustForm eventId={eventId} />
          </CardContent>
        </Card>
      )}

      <div className="rounded-lg border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950 overflow-x-auto">
        {error ? (
          <p className="p-6 text-sm text-red-600 dark:text-red-400">Failed to load: {error.message}</p>
        ) : isLoading ? (
          <div className="flex items-center justify-center py-16"><Spinner /></div>
        ) : rows.length === 0 ? (
          <p className="p-6 text-sm text-neutral-500 dark:text-neutral-400">
            {activeOnly ? 'Nobody is trusted at this event.' : 'Nothing recorded.'} Trust sponsors, staff and known regulars here so they are never challenged or refused;
            an organizer who wants fewer challenges for everyone picks the Relaxed profile on the Policy tab instead.
          </p>
        ) : (
          <table className="w-full whitespace-nowrap">
            <thead>
              <tr className="border-b border-neutral-200 dark:border-neutral-800">
                <th className={TH}>Who</th>
                <th className={TH}>Kind</th>
                <th className={TH}>Reason</th>
                <th className={TH}>Since</th>
                <th className={TH}>Expires</th>
                <th className={TH}>Status</th>
                {canManage && <th className={TH} />}
              </tr>
            </thead>
            <tbody>
              {rows.map((a) => (
                <tr key={a.id} className={ROW}>
                  <td className={TD}>
                    {describe(a)}
                    <div className="text-xs text-neutral-500 dark:text-neutral-400 font-mono">
                      {a.kind === 'attendee' ? (a.user ? `#${a.user}` : EMPTY) : a.instrument_id || EMPTY}
                    </div>
                  </td>
                  <td className={TD}>
                    <span className={cn(PILL, 'bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300')}>{kindLabel(a.kind)}</span>
                  </td>
                  <td className={cn(TD, 'whitespace-normal max-w-[20rem]')}>{a.reason || EMPTY}</td>
                  <td className={TD}>{fmtDateTime(a.created_at)}{a.created_by ? <div className="text-xs text-neutral-500">by #{a.created_by}</div> : null}</td>
                  <td className={TD}>{a.expires_at ? fmtDateTime(a.expires_at) : 'Never'}</td>
                  <td className={TD}>
                    {a.is_active
                      ? <span className={cn(PILL, 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400')}>Active</span>
                      : a.revoked_at
                        ? <span className={cn(PILL, 'bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300')}>Revoked {fmtDateTime(a.revoked_at)}</span>
                        : <span className={cn(PILL, 'bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300')}>Expired</span>}
                  </td>
                  {canManage && (
                    <td className={TD}>{a.is_active && <RevokeButton eventId={eventId} allowance={a} />}</td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
